/**
 * /particuliers/aides — Hub panoramique des aides à la rénovation
 * énergétique pour les particuliers.
 *
 * Refonte 2026 : entièrement bâtie sur le kit UI `audience/particulier`.
 * Sections :
 *  - Hero (PageHero + LiveCard "Cumul type")
 *  - Bandeau KPI animé
 *  - Grille comparative des 5 aides (custom — spécifique à cette page)
 *  - "Comment ça se cumule" + visualisation stack (custom)
 *  - Cross-link vers les 4 parcours (ParcoursNav)
 *  - FAQ (FaqSplit)
 *  - CTA final dark (CTAFinal)
 */

import { ArrowRight, CheckCircle2, Layers } from 'lucide-react';
import { Container } from '../../../components/layout/Container';
import { Reveal } from '../../../components/motion/Reveal';
import { StaggerGrid, StaggerItem } from '../../../components/motion/StaggerGrid';
import { CountUp } from '../../../components/motion/CountUp';
import { BreadcrumbListLd } from '../../../components/seo/jsonld/BreadcrumbList';
import { AIDES_PARTICULIERS } from '../../../config/aides-particuliers';
import { getIcon } from '../../../lib/lucide-icons';
import { cn } from '../../../lib/cn';
import {
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierFAQ,
  ParticulierKPIBand,
  ParticulierLiveCard,
  ParticulierPageHero,
  ParticulierParcoursNav,
  ParticulierSectionHeader,
  particulierPageUrl,
} from '../../../components/audience/particulier';

const KPI = [
  {
    value: 'Jusqu\'à 90%',
    label: "d'aides cumulables sur vos travaux",
    source: 'Pour les ménages aux revenus modestes',
  },
  {
    value: '5 aides',
    numericValue: 5,
    label: 'cumulables sur un même chantier',
    source: "MaPrimeRénov’ + CEE + éco-PTZ + TVA + Coup de pouce",
  },
  {
    value: '5,5 %',
    label: 'de TVA au lieu de 20 % standard',
    source: "Économie automatique sur la facture de l'artisan",
  },
];

const CUMUL_FAQ = [
  {
    question: 'Peut-on vraiment cumuler les 5 aides sur un même chantier ?',
    answer:
      "Oui dans la majorité des cas. MaPrimeRénov’, la Prime énergie (CEE), l'éco-PTZ, la TVA à 5,5 % et le Coup de pouce ont été conçus pour être cumulables. Notre calculateur cumule automatiquement toutes les aides auxquelles vous avez droit selon votre profil et vos travaux.",
  },
  {
    question: 'Dans quel ordre dois-je demander les aides ?',
    answer:
      "Il faut TOUJOURS engager les démarches (MaPrimeRénov’, Prime énergie) AVANT la signature du devis. C'est une règle d'or : une demande déposée après signature est systématiquement rejetée. L'éco-PTZ peut être demandé après, pour financer le reste à charge. La TVA à 5,5 % et le Coup de pouce s'appliquent automatiquement à la facturation.",
  },
  {
    question: 'Y a-t-il un plafond global de cumul ?',
    answer:
      "Pas de plafond unifié. Chaque dispositif a son propre plafond (50 000 € pour l'éco-PTZ rénovation globale, barèmes Anah pour MaPrimeRénov’, tarif marché pour les CEE). Mais une règle commune : le total des aides ne peut pas excéder le coût des travaux. Vous restez avec au minimum un reste à charge financé.",
  },
  {
    question: 'Faut-il faire appel à un artisan RGE pour toutes ces aides ?',
    answer:
      "Oui pour MaPrimeRénov’, les CEE et le Coup de pouce. L'éco-PTZ exige également un artisan RGE. Seule la TVA à 5,5 % n'impose pas la certification RGE — mais si vous voulez cumuler avec les autres dispositifs, l'artisan devra l'être pour ces aides-là.",
  },
  {
    question: "Mon Accompagnateur Rénov' est-il obligatoire pour cumuler les aides ?",
    answer:
      "Non, pas pour un cumul classique (parcours par geste). Le MAR est obligatoire uniquement pour MaPrimeRénov’ Parcours Accompagné (rénovations d'ampleur visant ≥ 2 sauts DPE). Pour des travaux par geste, vous pouvez cumuler MPR + CEE + éco-PTZ + TVA 5,5 % sans MAR.",
  },
  {
    question: 'Les aides locales sont-elles cumulables avec les aides nationales ?',
    answer:
      "Oui dans la quasi-totalité des cas. Les aides des collectivités (région, département, intercommunalité, commune) se cumulent avec MaPrimeRénov’, les CEE et l'éco-PTZ. Vérifiez les conditions auprès de votre Espace Conseil France Rénov' local.",
  },
];

export default function ParticuliersAidesHubPage() {
  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${PARTICULIER_URLS.site}/` },
          { name: 'Particuliers', url: `${PARTICULIER_URLS.site}${PARTICULIER_URLS.home}` },
          { name: 'Aides', url: particulierPageUrl('aides') },
        ]}
      />

      <ParticulierPageHero
        breadcrumb={[{ name: 'Aides' }]}
        eyebrow="Panorama 2026 · 5 aides cumulables"
        eyebrowIconName="sparkles"
        headline="Toutes vos aides"
        headlineAccent="à la rénovation énergétique"
        subheadline={
          <>
            MaPrimeRénov’, Prime énergie (CEE), éco-PTZ, TVA à 5,5 % et Coup de pouce :
            cinq dispositifs nationaux cumulables qui peuvent couvrir une part majoritaire
            de vos travaux. Voici comment ils fonctionnent et comment les combiner.
          </>
        }
        primaryCta={{
          label: 'Calculer mes aides en 2 min',
          href: PARTICULIER_URLS.calculateur,
        }}
        secondaryCta={{
          label: 'Voir les travaux éligibles',
          href: '/cee/fiches?secteur=BAR',
        }}
        bullets={['Gratuit · sans inscription', 'Résultat immédiat', 'Aides 2026 à jour']}
        sideSlot={
          <ParticulierLiveCard
            eyebrow="Cumul type · pompe à chaleur air/eau"
            subEyebrow="Ménage modeste · maison F → C"
            badge={
              <span className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-200/50">
                <span className="h-1 w-1 rounded-full bg-brand-500 animate-glow-pulse" />
                Exemple
              </span>
            }
          >
            <CumulTypeBody />
          </ParticulierLiveCard>
        }
      />

      <ParticulierKPIBand items={KPI} />

      <section className={PARTICULIER_SECTION.standard}>
        <Container>
          <ParticulierSectionHeader
            eyebrow="Les 5 dispositifs"
            title="Cliquez pour découvrir chaque aide en détail"
            description="Chaque guide détaille les montants, conditions, démarches et compatibilité de cumul."
            align="center"
          />

          <StaggerGrid className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AIDES_PARTICULIERS.map((aide) => {
              const Icon = getIcon(aide.iconName);
              return (
                <StaggerItem key={aide.slug}>
                  <a
                    href={`/particuliers/aides/${aide.slug}`}
                    className="group relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.12)]"
                  >
                    <div
                      aria-hidden
                      className={cn(
                        'absolute inset-x-0 top-0 h-px',
                        PARTICULIER_GRADIENT.cardTopHairline,
                      )}
                    />

                    <div className="flex items-start justify-between gap-3">
                      <span
                        className={cn(
                          'inline-flex h-12 w-12 items-center justify-center rounded-xl text-brand-700 ring-1 ring-inset ring-brand-200/60 transition-transform duration-200 ease-out group-hover:scale-105',
                          PARTICULIER_GRADIENT.iconHalo,
                        )}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.7} />
                      </span>
                      <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-stone-500">
                        {aide.eyebrow}
                      </span>
                    </div>

                    <div>
                      <p className="font-display text-xl font-bold text-ink">{aide.title}</p>
                      <p className="mt-1 text-[12px] text-stone-500">{aide.amountLabel}</p>
                    </div>

                    <p className="line-clamp-3 text-[13.5px] leading-relaxed text-stone-600">
                      {aide.lead}
                    </p>

                    <div className="mt-auto flex items-end justify-between pt-2">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                          Montant
                        </p>
                        <p
                          className={cn(
                            'mt-1 font-display text-lg font-bold tracking-tight tabular-nums',
                            PARTICULIER_GRADIENT.aideAmount,
                          )}
                        >
                          {aide.amount}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
                        Guide complet
                        <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                      </span>
                    </div>
                  </a>
                </StaggerItem>
              );
            })}
          </StaggerGrid>
        </Container>
      </section>

      <section className={cn(PARTICULIER_SECTION.alternateBg, PARTICULIER_SECTION.standard)}>
        <Container>
          <div className="mx-auto grid max-w-5xl gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
            <Reveal>
              <div>
                <ParticulierSectionHeader
                  eyebrow="Comment ça se cumule"
                  title="Empilez les aides pour réduire votre reste à charge"
                  description={
                    <>
                      Chaque dispositif fonctionne de manière indépendante mais cumulable.
                      L'ordre des démarches compte : engagez MaPrimeRénov’ et la Prime énergie{' '}
                      <span className="font-semibold text-ink">avant</span> de signer le devis.
                    </>
                  }
                  noReveal
                />

                <ul className="mt-6 space-y-2.5">
                  {[
                    "Demande MPR & CEE engagée avant signature du devis",
                    'Artisan certifié RGE pour les travaux concernés',
                    'Justificatifs conservés 5 ans (devis, factures, attestations)',
                    'Éco-PTZ disponible pour financer le reste à charge',
                  ].map((rule) => (
                    <li key={rule} className="flex items-start gap-2.5 text-[13.5px] text-stone-600">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" strokeWidth={2.2} />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <CumulStackVisualization />
            </Reveal>
          </div>
        </Container>
      </section>

      <ParticulierParcoursNav
        eyebrow="Parcours rénovation"
        title="De quel parcours partez-vous ?"
        description="Selon votre projet — un geste isolé ou une rénovation d'ampleur — le parcours diffère. Suivez le bon chemin pour mobiliser les bonnes aides."
        variant="compact"
      />

      <ParticulierFAQ
        eyebrow="FAQ cumul des aides"
        title="Vos questions sur le cumul des aides en 2026."
        items={CUMUL_FAQ}
      />

      <ParticulierCTAFinal
        badge="Calculateur 2 min · gratuit"
        title="Quelle aide vous"
        titleAccent="concerne précisément ?"
        description="Notre calculateur identifie les aides cumulables auxquelles vous avez droit selon vos travaux, votre logement et vos revenus — sources officielles citées."
        primaryCta={{
          label: 'Lancer le calculateur',
          href: PARTICULIER_URLS.calculateur,
        }}
        secondaryCta={{ label: 'Être rappelé(e)', href: PARTICULIER_URLS.contact }}
        bullets={['2 min · sans inscription', 'Aides 2026 à jour', 'Sources officielles citées']}
        layout="split"
        sideSlot={
          <ParticulierLiveCard
            variant="dark"
            withHalo={false}
            eyebrow="Cumul type · pompe à chaleur air/eau"
            subEyebrow="Ménage modeste · maison F → C"
            badge={
              <span className="inline-flex items-center gap-1 rounded-md bg-accent-500/15 px-2 py-0.5 text-[10px] font-semibold text-accent-300 ring-1 ring-inset ring-accent-300/30">
                <span className="h-1 w-1 rounded-full bg-accent-300 animate-glow-pulse" />
                Exemple
              </span>
            }
          >
            <CumulTypeBody dark />
          </ParticulierLiveCard>
        }
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// CumulTypeBody — corps du LiveCard "Cumul type" (light/dark)
// Spécifique à cette page car ses chiffres sont contextuels.
// ════════════════════════════════════════════════════════════════

function CumulTypeBody({ dark = false }: { dark?: boolean }) {
  const rows = [
    { label: "MaPrimeRénov’", value: 4000 },
    { label: 'Prime énergie (CEE)', value: 4200 },
    { label: 'Coup de pouce Chauffage', value: 1000 },
    { label: 'TVA 5,5 % vs 20 %', value: 2400 },
  ];
  const totalAides = rows.reduce((s, r) => s + r.value, 0);
  const coutTravauxTtc = 18000;
  const resteACharge = coutTravauxTtc - totalAides;
  const couvertureTotalePct = Math.round((totalAides / coutTravauxTtc) * 100);

  const valueLabelClass = dark ? 'text-ivory-50/80' : 'text-stone-700';
  const titleClass = dark ? 'text-ivory-50' : 'text-ink';
  const rowBgClass = dark
    ? 'border border-white/10 bg-white/[0.03]'
    : 'border border-stone-200/70 bg-stone-50/40';
  const dividerClass = dark ? 'border-white/10' : 'border-stone-200/60';
  const totalLabelClass = dark ? 'text-ivory-50/60' : 'text-stone-500';
  const totalValueClass = dark ? 'text-accent-300' : 'text-ink';

  return (
    <div>
      <div className="flex items-baseline gap-2 font-display tracking-tight">
        <span className={cn('text-3xl font-bold tabular-nums md:text-4xl', titleClass)}>
          <CountUp end={totalAides} suffix=" €" />
        </span>
        <span className={cn('text-xs', valueLabelClass)}>d'aides directes estimées</span>
      </div>

      <div className="mt-5 space-y-1.5">
        {rows.map((row) => (
          <div
            key={row.label}
            className={cn('flex items-center justify-between rounded-lg px-3 py-2', rowBgClass)}
          >
            <p className={cn('min-w-0 flex-1 truncate text-xs', valueLabelClass)}>{row.label}</p>
            <span className={cn('ml-2 font-mono text-xs font-semibold tabular-nums', titleClass)}>
              <CountUp end={row.value} suffix=" €" />
            </span>
          </div>
        ))}
      </div>

      <div className={cn('mt-5 flex items-center justify-between border-t pt-3', dividerClass)}>
        <p className={cn('text-[11px]', totalLabelClass)}>
          PAC ≈ {coutTravauxTtc.toLocaleString('fr-FR')} € TTC · reste à charge
        </p>
        <p className={cn('font-mono text-[12px] font-semibold tabular-nums', totalValueClass)}>
          {resteACharge.toLocaleString('fr-FR')} € · {couvertureTotalePct}% pris en charge
        </p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// CumulStackVisualization — visualisation "empilage" des 5 aides
// Spécifique à cette page (illustration pédagogique).
// ════════════════════════════════════════════════════════════════

function CumulStackVisualization() {
  const layers = [
    { name: "MaPrimeRénov’", sub: 'Forfait Anah selon vos revenus', amount: "Jusqu'à 5 000 €", tone: 'brand' },
    { name: 'Prime énergie (CEE)', sub: "Versée par l'obligé · sans condition", amount: '≈ 4 200 €', tone: 'accent' },
    { name: 'Coup de pouce', sub: 'Bonus CEE sur PAC, biomasse…', amount: '+ 500 à 1 000 €', tone: 'lavender' },
    { name: 'TVA à 5,5 %', sub: 'Auto · au lieu de 20 % standard', amount: '−2 400 €', tone: 'sage' },
    { name: 'Éco-PTZ', sub: 'Prêt à 0% pour le reste à charge', amount: "Jusqu'à 50 000 €", tone: 'ink' },
  ] as const;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone-200/70 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] md:p-8">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/40 via-transparent to-accent-50/30"
      />

      <div className="relative">
        <div className="mb-5 flex items-center gap-2 border-b border-stone-100 pb-4">
          <Layers className="h-4 w-4 text-brand-700" strokeWidth={1.8} />
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Empilage type pour une PAC air/eau
          </p>
        </div>

        <StaggerGrid className="space-y-2.5">
          {layers.map((layer, idx) => {
            const toneClass =
              layer.tone === 'brand'
                ? 'border-brand-200/70 bg-gradient-to-r from-brand-50 to-brand-50/30 text-brand-800 [&_.amount]:text-brand-800'
                : layer.tone === 'accent'
                  ? 'border-accent-200/70 bg-gradient-to-r from-accent-50 to-accent-50/30 text-accent-800 [&_.amount]:text-accent-800'
                  : layer.tone === 'lavender'
                    ? 'border-lavender-200/70 bg-gradient-to-r from-lavender-50 to-lavender-50/30 text-lavender-800 [&_.amount]:text-lavender-800'
                    : layer.tone === 'sage'
                      ? 'border-stone-200 bg-gradient-to-r from-stone-50 to-stone-50/30 text-stone-700 [&_.amount]:text-stone-700'
                      : 'border-stone-300 bg-gradient-to-r from-stone-100 to-stone-50 text-ink [&_.amount]:text-ink';

            return (
              <StaggerItem key={layer.name}>
                <div
                  className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${toneClass}`}
                  style={{ marginLeft: `${idx * 6}px` }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold">{layer.name}</p>
                    <p className="mt-0.5 text-[11px] opacity-70">{layer.sub}</p>
                  </div>
                  <span className="amount font-mono text-[13px] font-bold tabular-nums">
                    {layer.amount}
                  </span>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGrid>

        <div className="mt-5 rounded-xl bg-gradient-to-br from-ink to-stone-900 p-4 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory-50/60">
            Jusqu'à
          </p>
          <p className="mt-1 font-display text-3xl font-bold tracking-tight text-ivory-50 tabular-nums">
            <CountUp end={90} suffix="%" /> du coût pris en charge
          </p>
          <p className="mt-1 text-[11px] text-ivory-50/60">
            Pour les ménages modestes · cumul aides directes + financement éco-PTZ
          </p>
        </div>
      </div>
    </div>
  );
}
